"use client";

import { UserPlus } from "lucide-react";
import { motion } from "motion/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";

export type PreviewParticipant = Participant & {
  username?: string | null;
};

export type AmbientPreviewParticle = {
  id: string;
  left: string;
  top: string;
  className?: string;
  sizeClassName?: string;
  shadow?: string;
};

export type MatchPreviewState = "EMPTY" | "NO_SCORE" | "FULL";

const particleDriftVariants = {
  animate: (index: number) => ({
    y: [0, index % 2 === 0 ? -3 : 3, 0],
    x: [0, index % 3 === 0 ? 2 : -2, 0],
    transition: {
      duration: 6 + index * 2,
      repeat: Number.POSITIVE_INFINITY,
      ease: "easeInOut" as const,
    },
  }),
};

const winnerGlowAnimation = {
  opacity: [0.05, 0.16, 0.05],
  scale: [1, 1.03, 1],
};

const winnerScoreAnimation = {
  scale: [1, 1.012, 1],
  textShadow: [
    "0 0 6px rgba(0,0,0,0.22)",
    "0 0 14px rgba(255,255,255,0.1)",
    "0 0 6px rgba(0,0,0,0.22)",
  ],
};

const getHandle = (participant?: PreviewParticipant) =>
  participant?.handle ?? participant?.username ?? undefined;

type PreviewMetadataLabelProps = {
  label: string;
  className?: string;
  pillClassName?: string;
};

type PreviewMetadataTheme = {
  pillClassName: string;
  textClassName?: string;
};

const DEFAULT_PREVIEW_METADATA_THEME: PreviewMetadataTheme = {
  pillClassName:
    "border-glass-border-14 bg-black/42 shadow-[0_10px_24px_rgba(0,0,0,0.28)]",
  textClassName: "text-white/92",
};

const PREVIEW_METADATA_THEMES: Partial<Record<string, PreviewMetadataTheme>> = {
  Badminton: {
    pillClassName:
      "border-[#a6f0ff]/20 bg-[#003859]/78 shadow-[0_10px_28px_rgba(0,53,89,0.42)]",
    textClassName: "text-[#effcff]",
  },
  Basketball: {
    pillClassName:
      "border-[#ffc37a]/24 bg-[#6d4425]/80 shadow-[0_10px_28px_rgba(68,39,15,0.48)]",
    textClassName: "text-[#fff2df]",
  },
  Climbing: {
    pillClassName:
      "border-[#f0c38a]/20 bg-[#4b3120]/78 shadow-[0_10px_28px_rgba(49,31,20,0.46)]",
    textClassName: "text-[#fff1dc]",
  },
  Cricket: {
    pillClassName:
      "border-[#ffc6c6]/20 bg-[#5b1119]/78 shadow-[0_10px_28px_rgba(74,11,20,0.44)]",
    textClassName: "text-[#fff0f0]",
  },
  Cycling: {
    pillClassName:
      "border-[#9de8ff]/20 bg-[#0d3958]/78 shadow-[0_10px_28px_rgba(8,37,57,0.44)]",
    textClassName: "text-[#eefaff]",
  },
  "Day Retreats": {
    pillClassName:
      "border-[#f1d7a3]/24 bg-[#4d3526]/76 shadow-[0_10px_28px_rgba(61,39,24,0.42)]",
    textClassName: "text-[#fff4df] tracking-[0.24em]",
  },
  Football: {
    pillClassName:
      "border-[#bdf2c8]/20 bg-[#0c4a2a]/78 shadow-[0_10px_28px_rgba(8,58,31,0.44)]",
    textClassName: "text-[#eefdf1]",
  },
  Golf: {
    pillClassName:
      "border-[#d4f1a7]/20 bg-[#23451e]/78 shadow-[0_10px_28px_rgba(20,47,18,0.44)]",
    textClassName: "text-[#f5ffe7]",
  },
  Hyrox: {
    pillClassName:
      "border-[#ffb4a8]/20 bg-[#3a1517]/82 shadow-[0_10px_28px_rgba(42,12,14,0.48)]",
    textClassName: "text-[#fff1ee]",
  },
  MMA: {
    pillClassName:
      "border-[#ff9d9d]/22 bg-[#2b1111]/82 shadow-[0_10px_28px_rgba(26,8,8,0.5)]",
    textClassName: "text-[#fff1f1] tracking-[0.28em]",
  },
  Padel: {
    pillClassName:
      "border-[#93d9ff]/22 bg-[#0d3a60]/80 shadow-[0_10px_28px_rgba(8,35,57,0.46)]",
    textClassName: "text-[#eef8ff]",
  },
  Pickleball: {
    pillClassName:
      "border-[#bfe89d]/20 bg-[#0f4a2a]/78 shadow-[0_10px_28px_rgba(10,44,25,0.44)]",
    textClassName: "text-[#f5ffe9]",
  },
  Pilates: {
    pillClassName:
      "border-[#f3d7d7]/20 bg-[#5a3b3b]/74 shadow-[0_10px_28px_rgba(57,37,37,0.4)]",
    textClassName: "text-[#fff2ef]",
  },
  Recovery: {
    pillClassName:
      "border-[#b7e7df]/20 bg-[#274d4a]/74 shadow-[0_10px_28px_rgba(28,52,50,0.4)]",
    textClassName: "text-[#effffb]",
  },
  Running: {
    pillClassName:
      "border-[#ffc38a]/22 bg-[#5c2616]/78 shadow-[0_10px_28px_rgba(72,28,16,0.44)]",
    textClassName: "text-[#fff1e1]",
  },
  Snooker: {
    pillClassName:
      "border-[#d7f0bf]/20 bg-[#26572e]/80 shadow-[0_10px_28px_rgba(24,50,28,0.46)]",
    textClassName: "text-[#faf6dc]",
  },
  Squash: {
    pillClassName:
      "border-[#ffd39d]/22 bg-[#6a4621]/78 shadow-[0_10px_28px_rgba(71,45,18,0.46)]",
    textClassName: "text-[#fff2df]",
  },
  Swimming: {
    pillClassName:
      "border-[#94e5ff]/20 bg-[#0d4663]/76 shadow-[0_10px_28px_rgba(10,47,68,0.42)]",
    textClassName: "text-[#effcff]",
  },
  Tennis: {
    pillClassName:
      // Brighter rim + near-black fill: the court art now has a dark-green
      // back wall that the old green-on-green chip disappeared into.
      "border-[#dfff00]/45 bg-[#07140b]/90 shadow-[0_10px_28px_rgba(0,0,0,0.55)]",
    textClassName: "text-[#f6ffd7]",
  },
  Throwball: {
    pillClassName:
      "border-[#ffcf9f]/22 bg-[#6a2f21]/78 shadow-[0_10px_28px_rgba(70,31,18,0.44)]",
    textClassName: "text-[#fff1e2]",
  },
  "Table Tennis": {
    pillClassName:
      "border-[#8fd1ff]/22 bg-[#0c3d61]/80 shadow-[0_10px_28px_rgba(8,37,58,0.46)]",
    textClassName: "text-[#eef8ff] tracking-[0.24em]",
  },
  Volleyball: {
    pillClassName:
      "border-[#ffceb7]/20 bg-[#80421e]/76 shadow-[0_10px_28px_rgba(86,44,18,0.42)]",
    textClassName: "text-[#fff2e6]",
  },
  Walking: {
    pillClassName:
      "border-[#cce7b6]/20 bg-[#35512b]/74 shadow-[0_10px_28px_rgba(34,52,26,0.4)]",
    textClassName: "text-[#f7ffe9]",
  },
  "Weight Training": {
    pillClassName:
      "border-[#ffb0b0]/20 bg-[#251719]/82 shadow-[0_10px_28px_rgba(24,12,13,0.5)]",
    textClassName: "text-[#fff0ee] text-[7px] tracking-[0.24em]",
  },
  Yoga: {
    pillClassName:
      "border-[#f0c8d8]/20 bg-[#5b3755]/74 shadow-[0_10px_28px_rgba(56,33,52,0.4)]",
    textClassName: "text-[#fff1f8]",
  },
  HIIT: {
    pillClassName:
      "border-[#ffad9b]/20 bg-[#541a10]/78 shadow-[0_10px_28px_rgba(68,26,16,0.44)]",
    textClassName: "text-[#fff0ed]",
  },
  "Hot Yoga": {
    pillClassName:
      "border-[#ffcfa0]/20 bg-[#5c2d10]/78 shadow-[0_10px_28px_rgba(72,37,16,0.44)]",
    textClassName: "text-[#fff2e8]",
  },
  Nutrition: {
    pillClassName:
      "border-[#b8ffcf]/20 bg-[#10542d]/78 shadow-[0_10px_28px_rgba(16,68,37,0.44)]",
    textClassName: "text-[#edfdf3]",
  },
  "Scuba Diving": {
    pillClassName:
      "border-[#9be5ff]/20 bg-[#0c3854]/78 shadow-[0_10px_28px_rgba(10,47,68,0.44)]",
    textClassName: "text-[#edfaff]",
  },
  Snorkeling: {
    pillClassName:
      "border-[#9efff2]/20 bg-[#0c544d]/78 shadow-[0_10px_28px_rgba(10,68,62,0.44)]",
    textClassName: "text-[#edfffc]",
  },
  Trekking: {
    pillClassName:
      "border-[#cbe59e]/20 bg-[#2b4d1b]/78 shadow-[0_10px_28px_rgba(35,55,23,0.42)]",
    textClassName: "text-[#f5ffe8]",
  },
  "Animal Flow": {
    pillClassName:
      "border-[#ffd2ab]/20 bg-[#54351c]/78 shadow-[0_10px_28px_rgba(68,44,25,0.42)]",
    textClassName: "text-[#fff4eb]",
  },
  Bouldering: {
    pillClassName:
      "border-[#dfdfdf]/20 bg-[#3a3d40]/78 shadow-[0_10px_28px_rgba(50,53,55,0.42)]",
    textClassName: "text-[#f5f5f5]",
  },
  Stretching: {
    pillClassName:
      "border-[#e5c2ff]/20 bg-[#421d5a]/78 shadow-[0_10px_28px_rgba(50,23,68,0.42)]",
    textClassName: "text-[#f9f0ff]",
  },
};

export function PreviewMetadataLabel({
  label,
  className,
  pillClassName,
}: PreviewMetadataLabelProps) {
  const theme =
    PREVIEW_METADATA_THEMES[label] ?? DEFAULT_PREVIEW_METADATA_THEME;

  return (
    <div className="pointer-events-none absolute top-[6px] left-1/2 z-20 -translate-x-1/2 sm:top-[8px]">
      <div
        className={cn(
          "inline-flex items-center rounded-full border px-4 py-1.5 shadow-[0_14px_30px_rgba(0,0,0,0.34)] backdrop-blur-md sm:px-5 sm:py-2",
          theme.pillClassName,
          pillClassName,
        )}
      >
        <span
          className={cn(
            "text-[13px] leading-none font-black tracking-[0.24em] whitespace-nowrap uppercase [text-shadow:0_1px_1px_rgba(0,0,0,0.32)] sm:text-[11px]",
            theme.textClassName,
            className,
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

type PreviewWaitingStateProps = {
  className?: string;
};

function PreviewWaitingState({ className }: PreviewWaitingStateProps) {
  return (
    <div
      className={cn(
        "absolute inset-0 z-10 flex items-center justify-center opacity-30",
        className,
      )}
    >
      <span className="flex items-center gap-4 text-[10px] font-semibold tracking-[0.4em] text-white uppercase">
        <div className="h-[1px] w-8 bg-white/20" />
        Waiting
        <div className="h-[1px] w-8 bg-white/20" />
      </span>
    </div>
  );
}

type AmbientPreviewParticlesProps = {
  particles: AmbientPreviewParticle[];
};

function AmbientPreviewParticles({ particles }: AmbientPreviewParticlesProps) {
  if (particles.length === 0) return null;

  return (
    <>
      {particles.map((particle, index) => (
        <motion.div
          key={particle.id}
          custom={index}
          initial={{ scale: 0.8, opacity: 0 }}
          animate="animate"
          variants={particleDriftVariants}
          whileInView={{
            scale: 1,
            opacity: 1,
            transition: { type: "spring", stiffness: 100, delay: index * 0.1 },
          }}
          className={cn(
            "absolute z-0 h-[10px] w-[10px] rounded-full opacity-80",
            particle.className,
            particle.sizeClassName,
          )}
          style={{
            left: particle.left,
            top: particle.top,
            boxShadow:
              particle.shadow ??
              "inset -2px -2px 4px rgba(0,0,0,0.3), inset 1px 1px 2px rgba(255,255,255,0.8), 2px 3px 6px rgba(0,0,0,0.6)",
          }}
        >
          <div className="absolute top-[20%] left-[20%] h-[30%] w-[30%] rounded-full bg-white/50 blur-[1px]" />
        </motion.div>
      ))}
    </>
  );
}

type PreviewAvatarGroupProps = {
  participants: PreviewParticipant[];
  maxVisible?: number;
  overflowWithinMaxVisible?: boolean;
  linkToProfiles?: boolean;
  avatarClassName?: string;
  overflowClassName?: string;
  avatarFallbackClassName?: string;
  groupClassName?: string;
  emphasize?: boolean;
};

export function PreviewAvatarGroup({
  participants,
  maxVisible = 5,
  overflowWithinMaxVisible = false,
  linkToProfiles = false,
  avatarClassName,
  overflowClassName,
  avatarFallbackClassName,
  groupClassName,
  emphasize = false,
}: PreviewAvatarGroupProps) {
  const visibleLimit =
    overflowWithinMaxVisible && participants.length > maxVisible
      ? Math.max(maxVisible - 1, 1)
      : maxVisible;
  const visibleParticipants = participants.slice(0, visibleLimit);
  const overflowCount = Math.max(
    participants.length - visibleParticipants.length,
    0,
  );

  return (
    <div
      className={cn(
        "flex items-center -space-x-2.5 sm:-space-x-3 rtl:space-x-reverse",
        groupClassName,
      )}
    >
      {visibleParticipants.map((participant) => {
        const content = (
          <Avatar
            className={cn(
              "relative h-14 w-14 shrink-0 border bg-black/35 shadow-lg ring-2 transition-all duration-300",
              emphasize
                ? "border-glass-border-20 ring-white/25"
                : "border-glass-border-10 ring-black/35",
              avatarClassName,
            )}
          >
            <AvatarImage src={participant.avatarUrl ?? undefined} />
            <AvatarFallback
              className={cn(
                // Frosted, not a black hole punched in the artwork.
                // Its own opaque dark base: the theme's fallback fill turns light
                // in the light app theme (white initials on near-white), and a
                // translucent one let busy scene art bleed through the letters.
                "rounded-none bg-neutral-700 bg-linear-to-br from-white/25 to-white/0 text-xs font-semibold text-white dark:bg-neutral-700",
                avatarFallbackClassName,
              )}
            >
              {participant.displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        );

        const handle = getHandle(participant);

        if (linkToProfiles && handle) {
          return (
            <BackAwareLink
              key={participant.id}
              href={`/profile/${handle}`}
              aria-label={`View ${participant.displayName}'s profile`}
            >
              {content}
            </BackAwareLink>
          );
        }

        return <div key={participant.id}>{content}</div>;
      })}

      {overflowCount > 0 ? (
        <div
          className={cn(
            "relative flex h-14 w-14 shrink-0 items-center justify-center rounded-[30%] border border-glass-border-10 bg-black/45 shadow-lg ring-2 ring-black/35 backdrop-blur-sm",
            overflowClassName,
          )}
        >
          <span className="text-[10px] font-bold text-white/80">
            +{overflowCount}
          </span>
        </div>
      ) : null}
    </div>
  );
}

type ActivityScenePreviewProps = {
  label: string;
  backgroundClassName: string;
  participants: PreviewParticipant[];
  scene: React.ReactNode;
  overlays?: React.ReactNode;
  ambientParticles?: AmbientPreviewParticle[];
  emptyDecor?: React.ReactNode;
  activeDecor?: React.ReactNode;
  className?: string;
  linkToProfiles?: boolean;
  metadataClassName?: string;
  /** @deprecated nudged the old centred stack; ignored by the bottom pill. */
  participantClassName?: string;
  /** @deprecated the participant pill has no glow; kept for callers. */
  participantGlowClassName?: string;
};

export function ActivityScenePreview({
  label,
  backgroundClassName,
  participants,
  scene,
  overlays,
  ambientParticles = [],
  emptyDecor,
  activeDecor,
  className,
  linkToProfiles = false,
  metadataClassName,
}: ActivityScenePreviewProps) {
  const isEmpty = participants.length === 0;

  return (
    <div
      className={cn(
        "group relative h-[220px] w-full overflow-hidden rounded-3xl border border-white/5 shadow-2xl select-none",
        backgroundClassName,
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-0 z-0">{scene}</div>
      {overlays}

      <PreviewMetadataLabel label={label} className={metadataClassName} />

      {isEmpty ? (
        <>
          <PreviewWaitingState />
          {emptyDecor}
        </>
      ) : (
        <>
          <AmbientPreviewParticles particles={ambientParticles} />
          {activeDecor}
          {/* Who's in rides a frosted pill along the bottom edge, so the
              scene itself — the eye-catching part — stays unobstructed. */}
          <div className="absolute inset-x-0 bottom-3 z-20 flex justify-center">
            {/* `participantClassName` is deliberately NOT applied: scenes
                passed nudges like `translate-y-5` for the old centred stack,
                which push this edge-anchored pill out of the frame. */}
            <div className="flex items-center rounded-full bg-black/35 p-1 shadow-[0_8px_24px_rgba(0,0,0,0.35)] ring-1 ring-white/15 backdrop-blur-md backdrop-saturate-150">
              <PreviewAvatarGroup
                participants={participants}
                maxVisible={4}
                overflowWithinMaxVisible
                linkToProfiles={linkToProfiles}
                avatarClassName="h-9 w-9 border-white/25 shadow-none ring-0"
                overflowClassName="h-9 w-9 border-white/20 bg-white/15 shadow-none ring-0"
                groupClassName="-space-x-2 sm:-space-x-2"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

type MatchParticipantAnchorProps = {
  team: PreviewParticipant[];
  isWinner?: boolean;
  linkToProfiles?: boolean;
  fallbackLabel: string;
  maxVisible?: number;
  overflowWithinMaxVisible?: boolean;
  avatarClassName?: string;
  className?: string;
};

export function MatchParticipantAnchor({
  team,
  isWinner,
  linkToProfiles = false,
  fallbackLabel: _fallbackLabel,
  maxVisible = 3,
  overflowWithinMaxVisible = false,
  avatarClassName,
  className,
}: MatchParticipantAnchorProps) {
  if (team.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 opacity-30">
        <div className="flex h-14 w-14 items-center justify-center rounded-[30%] border border-dashed border-white">
          <UserPlus className="h-4 w-4 text-white" />
        </div>
      </div>
    );
  }

  const visibleCount = maxVisible;

  const content = (
    <div
      className={cn(
        "relative z-30 flex w-fit flex-col items-center justify-center gap-1.5",
        className,
      )}
    >
      <div className="relative flex justify-center">
        {isWinner ? (
          <motion.div
            animate={winnerGlowAnimation}
            transition={{
              duration: 4,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
            className="pointer-events-none absolute inset-[-10px] rounded-full bg-white blur-[8px]"
          />
        ) : null}

        <PreviewAvatarGroup
          participants={team}
          maxVisible={visibleCount}
          overflowWithinMaxVisible={overflowWithinMaxVisible}
          avatarClassName={cn(
            "transition-all duration-500",
            isWinner ? "scale-100" : "scale-[0.97] opacity-[0.92]",
            avatarClassName,
          )}
          emphasize={!!isWinner}
          groupClassName="justify-center"
        />
      </div>
    </div>
  );

  if (linkToProfiles && team[0] && getHandle(team[0])) {
    return (
      <BackAwareLink
        href={`/profile/${getHandle(team[0])}`}
        aria-label={`View ${team[0].displayName}'s profile`}
        className="block w-fit"
      >
        {content}
      </BackAwareLink>
    );
  }

  return content;
}

type MatchPreviewShellProps = {
  label: string;
  backgroundClassName: string;
  matchState: MatchPreviewState;
  liftAnchorsOnFullScore?: boolean;
  scene: React.ReactNode;
  yourAnchor: React.ReactNode;
  opponentAnchor: React.ReactNode;
  centerContent?: React.ReactNode;
  emptyDecor?: React.ReactNode;
  noScoreDecor?: React.ReactNode;
  fullDecor?: React.ReactNode;
  className?: string;
  metadataClassName?: string;
};

export function MatchPreviewShell({
  label,
  backgroundClassName,
  matchState,
  liftAnchorsOnFullScore = false,
  scene,
  yourAnchor,
  opponentAnchor,
  centerContent,
  emptyDecor,
  noScoreDecor,
  fullDecor,
  className,
  metadataClassName,
}: MatchPreviewShellProps) {
  const anchorPositionClass =
    matchState === "FULL" && liftAnchorsOnFullScore
      ? "top-7 sm:top-8"
      : "top-1/2 -translate-y-1/2";

  return (
    <div
      className={cn(
        "group relative h-[220px] w-full overflow-hidden rounded-3xl border border-white/5 shadow-2xl select-none",
        backgroundClassName,
        className,
      )}
    >
      {scene}
      <PreviewMetadataLabel label={label} className={metadataClassName} />

      {matchState === "EMPTY" ? <PreviewWaitingState /> : null}
      {matchState === "EMPTY" ? emptyDecor : null}
      {matchState === "NO_SCORE" ? noScoreDecor : null}
      {matchState === "FULL" ? fullDecor : null}

      <div className="pointer-events-none absolute inset-0 z-20">
        <div
          className={cn(
            "pointer-events-auto absolute left-4 sm:left-6",
            anchorPositionClass,
          )}
        >
          {yourAnchor}
        </div>
        <div className="pointer-events-auto absolute inset-0 flex items-center justify-center">
          {centerContent}
        </div>
        <div
          className={cn(
            "pointer-events-auto absolute right-4 sm:right-6",
            anchorPositionClass,
          )}
        >
          {opponentAnchor}
        </div>
      </div>
    </div>
  );
}

type MatchPreviewScoreProps = {
  transitionKey: string | number;
  leftValue: React.ReactNode;
  rightValue: React.ReactNode;
  leftWinner?: boolean;
  rightWinner?: boolean;
  detail?: React.ReactNode;
  className?: string;
  detailWrapperClassName?: string;
  valueClassName?: string;
  leftClassName?: string;
  rightClassName?: string;
  delimiter?: React.ReactNode;
  delimiterClassName?: string;
};

export function MatchPreviewScore({
  transitionKey,
  leftValue,
  rightValue,
  leftWinner,
  rightWinner,
  detail,
  className,
  detailWrapperClassName,
  valueClassName,
  leftClassName,
  rightClassName,
  delimiter = "-",
  delimiterClassName,
}: MatchPreviewScoreProps) {
  return (
    <div className={cn("relative flex flex-col items-center", className)}>
      <motion.div
        key={transitionKey}
        initial={{ scale: 0.96, filter: "blur(2px)" }}
        animate={{ scale: 1, filter: "blur(0px)" }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="flex items-center"
      >
        <motion.span
          animate={leftWinner ? winnerScoreAnimation : {}}
          transition={{
            duration: 4,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
          className={cn(
            "block w-[70px] text-right text-[75px] leading-none font-black tracking-tighter text-white/80 drop-shadow-lg transition-all duration-700 sm:w-[85px] sm:text-[90px]",
            leftWinner && "text-white",
            valueClassName,
            leftClassName,
          )}
        >
          {leftValue}
        </motion.span>

        <span
          className={cn(
            "pt-1 text-[40px] font-light text-white/80 mix-blend-overlay sm:text-[50px]",
            delimiterClassName,
          )}
        >
          {delimiter}
        </span>

        <motion.span
          animate={rightWinner ? winnerScoreAnimation : {}}
          transition={{
            duration: 4,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
          className={cn(
            "block w-[70px] text-left text-[75px] leading-none font-black tracking-tighter text-white/80 drop-shadow-lg transition-all duration-700 sm:w-[85px] sm:text-[90px]",
            rightWinner && "text-white",
            valueClassName,
            rightClassName,
          )}
        >
          {rightValue}
        </motion.span>
      </motion.div>

      {detail ? (
        <div
          className={cn(
            "absolute top-full mt-2 flex items-center justify-center",
            detailWrapperClassName,
          )}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}
